import {
  GET_AGGREGATED_DATA_ACTION,
  GET_COLLECTIONS_ACTION,
  SELECT_COLLECTION_ACTION,
  PUBLISH_AGGREGATION_ACTION,
  CLUSTER_AGGREGATION_ACTION,
  GET_CLUSTER_FACETS_ACTION,
  GET_ADVANCE_CLUSTER_AGGREGATION_ACTION,
  GET_ADVANCE_CLUSTER_FACETS_ACTION,
  AGGREGATION_PAGINATED_ACTION,
  SINGLE_RECOMMENDATION_ACTION,
  RECOMMENDATION_CONFIG_ACTION,
  RECOMMENDATION_SEARCH_RESULTS_ACTION,
} from "./collection.action";
import ReducerFactory from "../reducer-factory";
import { ActionFunction1, ActionFunctionAny } from "redux-actions";
import { Action } from "redux";
import {
  IAdvanceClusterAggregationResponse,
  IAdvanceClusterFacetsResponse,
  IClusterAggregationResponse,
  IClusterFacetsResponse,
  ICollection,
  ICollectionAggregateData,
  IPaginated,
  IPublishedAggregationResponse,
  IRecommendationConfig,
  IRecommendationSearchResults,
  ISingleProductRecommendation,
} from "../../types";

interface IData<T> {
  loading: boolean;
  initialized: boolean;
  error: any;
  data: T;
}

export interface ICollectionState {
  collections: IData<string[]>;
  selectedCollection: IData<ICollection | null>;
  aggregatedData: IData<ICollectionAggregateData[]>;
  paginatedAggregatedData: IPaginated<ICollectionAggregateData[]>;
  publishedAggregation: IData<IPublishedAggregationResponse | null>;
  clusterAggregation: IData<IClusterAggregationResponse | null>;
  clusterFacets: IData<IClusterFacetsResponse | null>;
  advanceClusterAggregation: IData<IAdvanceClusterAggregationResponse | null>;
  advanceClusterFacets: IData<IAdvanceClusterFacetsResponse | null>;
  singleRecommendation: IData<ISingleProductRecommendation | null>;
  recommendationConfig: IRecommendationConfig;
  recommendationSearchResults: IData<IRecommendationSearchResults | null>;
}

export const initState: ICollectionState = {
  collections: {
    loading: false,
    initialized: false,
    error: null,
    data: [],
  },
  aggregatedData: {
    loading: false,
    initialized: false,
    error: null,
    data: [],
  },
  paginatedAggregatedData: {
    currentPage: 1,
    data: [],
    total: 0,
    pageSize: 20,
  },
  selectedCollection: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  publishedAggregation: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  clusterAggregation: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  clusterFacets: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  advanceClusterAggregation: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  advanceClusterFacets: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  singleRecommendation: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
  recommendationConfig: {
    approx: 0,
    hundred_scale: false,
    include_count: true,
    include_vector: false,
    min_score: 0,
    metric: "cosine",
    page: 1,
    page_size: 10,
    search_field: "",
    sum_fields: true,
    document_id: "",
    type: {
      label: "Single Document",
      value: 'SINGLE_PRODUCT_RECOMMENDATION',
    },
    vector_operation: {
      label: "Mean",
      value: "mean",
    },
    setup: true,
    document_ids: [],
    positive_document_ids: [],
    negative_document_ids: [],
  },
  recommendationSearchResults: {
    loading: false,
    initialized: false,
    error: null,
    data: null,
  },
};

export interface IActions<T> {
  reset: ActionFunctionAny<Action<any>>;
  init: ActionFunctionAny<Action<any>>;
  failed: ActionFunction1<any, Action<any>>;
  success: ActionFunction1<T, Action<T>>;
}

export const addDataActions = <T>(actions: IActions<T>, key: string) => {
  return (reducerFactory: ReducerFactory<any, any>) => {
    reducerFactory.add(actions.reset.toString(), (state) => {
      return {
        ...state,
        [key]: {
          initialized: false,
          loading: false,
          error: null,
          data: null,
        },
      };
    });

    reducerFactory.add(actions.init.toString(), (state) => {
      return {
        ...state,
        [key]: {
          initialized: true,
          loading: true,
          error: null,
          data: null,
        },
      };
    });

    reducerFactory.add(actions.failed.toString(), (state, action) => {
      return {
        ...state,
        [key]: {
          initialized: true,
          loading: false,
          error: action.payload,
          data: null,
        },
      };
    });

    reducerFactory.add(actions.success.toString(), (state, action) => {
      return {
        ...state,
        [key]: {
          initialized: true,
          loading: false,
          error: null,
          data: action.payload,
        },
      };
    });
  };
};

export const addAction = (actions: any, key: string) => {
  return (reducerFactory: ReducerFactory<any, any>) => {
    reducerFactory.add(actions.toString(), (state, action) => {
      return {
        ...state,
        [key]: action.payload,
      };
    });
  };
};

const reducer = new ReducerFactory<ICollectionState>(initState)
  .addCustom(
    addDataActions<ICollection>(SELECT_COLLECTION_ACTION, "selectedCollection")
  )
  .addCustom(addAction(AGGREGATION_PAGINATED_ACTION, "paginatedAggregatedData"))
  .addCustom(addDataActions<string[]>(GET_COLLECTIONS_ACTION, "collections"))
  .addCustom(
    addDataActions<IAdvanceClusterAggregationResponse>(
      GET_ADVANCE_CLUSTER_AGGREGATION_ACTION,
      "advanceClusterAggregation"
    )
  )
  .addCustom(
    addDataActions<IAdvanceClusterFacetsResponse>(
      GET_ADVANCE_CLUSTER_FACETS_ACTION,
      "advanceClusterFacets"
    )
  )
  .addCustom(
    addDataActions<IPublishedAggregationResponse>(
      PUBLISH_AGGREGATION_ACTION,
      "publishedAggregation"
    )
  )
  .addCustom(
    addDataActions<IClusterAggregationResponse>(
      CLUSTER_AGGREGATION_ACTION,
      "clusterAggregation"
    )
  )
  .addCustom(
    addDataActions<IClusterFacetsResponse>(
      GET_CLUSTER_FACETS_ACTION,
      "clusterFacets"
    )
  )
  .addCustom(
    addDataActions<ISingleProductRecommendation>(
      SINGLE_RECOMMENDATION_ACTION,
      "singleRecommendation"
    )
  )
  .addCustom(addAction(RECOMMENDATION_CONFIG_ACTION, "recommendationConfig"))
  .addCustom(
    addDataActions<ICollectionAggregateData[]>(
      GET_AGGREGATED_DATA_ACTION,
      "aggregatedData"
    )
  )
  .addCustom(
    addDataActions<IRecommendationSearchResults>(
      RECOMMENDATION_SEARCH_RESULTS_ACTION,
      "recommendationSearchResults"
    )
  )
  .toReducer();

export default reducer;
